import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const STORAGE_EXPANDED = "yokolicious.sidebar.expandedSections";
const STORAGE_COLLAPSED = "yokolicious.sidebar.collapsed";
// Only the first two sections ("Overview", "Workforce") are expanded out of the box - every
// other section starts collapsed until the user opens it (or the active route auto-expands
// its own section, or Expand All is used).
const DEFAULT_EXPANDED_SECTIONS = ["overview", "workforce"];

const SidebarContext = createContext(null);

const readExpanded = () => {
  const raw = localStorage.getItem(STORAGE_EXPANDED);
  if (raw === null) return new Set(DEFAULT_EXPANDED_SECTIONS);
  try {
    return new Set(JSON.parse(raw));
  } catch {
    return new Set(DEFAULT_EXPANDED_SECTIONS);
  }
};

export const SidebarProvider = ({ children }) => {
  const [expanded, setExpanded] = useState(readExpanded);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(STORAGE_COLLAPSED) === "true");
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_EXPANDED, JSON.stringify(Array.from(expanded)));
  }, [expanded]);

  useEffect(() => {
    localStorage.setItem(STORAGE_COLLAPSED, String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const toggleSection = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const expandSection = useCallback((id) => {
    setExpanded((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);

  const expandAllSections = useCallback((ids) => setExpanded(new Set(ids)), []);
  const collapseAllSections = useCallback(() => setExpanded(new Set()), []);

  const isExpanded = useCallback((id) => expanded.has(id), [expanded]);

  return (
    <SidebarContext.Provider value={{ isExpanded, toggleSection, expandSection, expandAllSections, collapseAllSections, collapsed, setCollapsed, mobileOpen, setMobileOpen }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebarState = () => {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error("useSidebarState must be used within a SidebarProvider");
  return ctx;
};
